import {
  And,
  Not,
  NumberEquals,
  NumberGreaterThan,
  StringContainsFold,
  StringEquals,
  StringListContains,
  StringListContainsAll,
  TimeAfter,
  TimeBefore,
} from '@vendasta/automata';
import { PipelineSelectorComponent } from '../../common/shared-form-elements';
import { PipelineSelectorDisplayService } from '../../common/shared-form-elements/pipeline-selector/pipeline-selector-display.service';
import { AccountOptionsComponent, AccountOptionsDisplayService } from './dynamic-options';
import { CurrencyInputDisplayService } from './dynamic-options/currency-input/currency-input-display.service';
import { CurrencyInputComponent } from './dynamic-options/currency-input/currency-input.component';
import { DateInputDisplayService } from './dynamic-options/date-input/date-input-display.service';
import { DateInputComponent } from './dynamic-options/date-input/date-input.component';
import { FreeTextDisplayService } from './dynamic-options/free-text-input/free-text-display.service';
import { FreeTextInputComponent } from './dynamic-options/free-text-input/free-text-input.component';
import { OpportunityStatusOptionsDisplayService } from './dynamic-options/opportunity-status-options';
import { OpportunityStatusOptionsComponent } from './dynamic-options/opportunity-status-options/opportunity-status-options-component';
import { OpportunityTagOptionsComponent } from './dynamic-options/opportunity-tag-options';
import { PackageOptionsComponent } from './dynamic-options/package-options';
import { PackageOptionsDisplayService } from './dynamic-options/package-options/package-options-display.service';
import { PositiveIntegerDisplayService } from './dynamic-options/positive-integer/positive-integer-display.service';
import { PositiveIntegerComponent } from './dynamic-options/positive-integer/positive-integer.component';
import { TagOptionsDisplayService } from './dynamic-options/tag-options/tag-options-display.service';
import { DataFilter } from './interface';

const CREATED_DATE = '{.opportunity_details.created}';

class CreatedDateDataFilter implements DataFilter {
  rules = [new TimeAfter(CREATED_DATE), new TimeBefore(CREATED_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.CREATED_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const EXPECTED_CLOSE_DATE = '{.opportunity_details.expected_close_date}';

class ExpectedCloseDateDataFilter implements DataFilter {
  rules = [new TimeAfter(EXPECTED_CLOSE_DATE), new TimeBefore(EXPECTED_CLOSE_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.OPPORTUNITY_DATA.DATA_LABELS.EXPECTED_CLOSE_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const TAGS = '{.opportunity_details.tags}';

class TagDataFilter implements DataFilter {
  rules = [
    new StringListContains(TAGS),
    new StringListContainsAll(TAGS),
    new Not(new StringListContains(TAGS)),
    new Not(new StringListContainsAll(TAGS)),
  ];
  dataEntryComponent = OpportunityTagOptionsComponent;
  displayService = TagOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.TAGS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

const ATTACHMENT_COUNT = '{.computed_properties.attachment_count}';

class AttachmentCountDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(ATTACHMENT_COUNT)),
    new NumberEquals(ATTACHMENT_COUNT),
    new NumberGreaterThan(ATTACHMENT_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.ATTACHMENT_COUNT';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const NOTES = '{.opportunity_details.notes}';

class NotesDataFilter implements DataFilter {
  rules = [new StringContainsFold(NOTES), new Not(new StringContainsFold(NOTES))];
  dataEntryComponent = FreeTextInputComponent;
  displayService = FreeTextDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.NOTES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.CONTAINS',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.DOES_NOT_CONTAIN',
  ];
}

const STATUS = '{.opportunity_details.pipeline_stage}';

class StatusDataFilter implements DataFilter {
  rules = [new StringEquals(STATUS)];
  dataEntryComponent = OpportunityStatusOptionsComponent;
  displayService = OpportunityStatusOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.STATUS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
}

const PROJECTED_FIRST_YEAR_VALUE = '{.opportunity_details.projected_first_year_value}';

class ProjectedFirstYearValueDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(PROJECTED_FIRST_YEAR_VALUE)),
    new NumberGreaterThan(PROJECTED_FIRST_YEAR_VALUE),
  ];
  dataEntryComponentList = [CurrencyInputComponent, CurrencyInputComponent];
  displayService = CurrencyInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.OPPORTUNITY_DATA.DATA_LABELS.PROJECTED_FIRST_YEAR_VALUE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
  ];
}

// This key is not used for comparisons since this filter is composed of a multi-valued AND rule
const PIPELINE_STAGE_RULE = '{.opportunity_details.pipeline_id}{.opportunity_details.probability}';
const PIPELINE_STAGE_CHILD_RULES = [
  new StringEquals('{.opportunity_details.pipeline_id}', '', ''),
  new NumberEquals('{.opportunity_details.probability}', '', ''),
];

export class PipelineStageDataFilter implements DataFilter {
  rules = [new And(PIPELINE_STAGE_CHILD_RULES), new Not(new And(PIPELINE_STAGE_CHILD_RULES))];
  dataEntryComponent = PipelineSelectorComponent;
  displayService = PipelineSelectorDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.OPPORTUNITY_DATA.DATA_LABELS.PIPELINE_AND_STAGE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
  updateComponentInstanceFn = (component: PipelineSelectorComponent) => {
    component.includeAnyStageOption = true;
  };
}

const PRODUCTS = '{.computed_properties.product_ids}';

export class ProductDataFilter implements DataFilter {
  rules = [
    new StringListContains(PRODUCTS),
    new StringListContainsAll(PRODUCTS),
    new Not(new StringListContains(PRODUCTS)),
    new Not(new StringListContainsAll(PRODUCTS)),
  ];
  dataEntryComponent = AccountOptionsComponent;
  displayService = AccountOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.OPPORTUNITY_DATA.DATA_LABELS.PRODUCTS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

const PACKAGES = '{.computed_properties.package_ids}';

export class PackageDataFilter implements DataFilter {
  rules = [
    new StringListContains(PACKAGES),
    new StringListContainsAll(PACKAGES),
    new Not(new StringListContains(PACKAGES)),
    new Not(new StringListContainsAll(PACKAGES)),
  ];
  dataEntryComponent = PackageOptionsComponent;
  displayService = PackageOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.OPPORTUNITY_DATA.DATA_LABELS.PACKAGES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

export const OPPORTUNITY_DATA_COMPONENTS: Map<string, DataFilter> = new Map<string, DataFilter>([
  [CREATED_DATE, new CreatedDateDataFilter()],
  [EXPECTED_CLOSE_DATE, new ExpectedCloseDateDataFilter()],
  [TAGS, new TagDataFilter()],
  [ATTACHMENT_COUNT, new AttachmentCountDataFilter()],
  [NOTES, new NotesDataFilter()],
  [PROJECTED_FIRST_YEAR_VALUE, new ProjectedFirstYearValueDataFilter()],
  [PIPELINE_STAGE_RULE, new PipelineStageDataFilter()],
  [PRODUCTS, new ProductDataFilter()],
  [PACKAGES, new PackageDataFilter()],
  [STATUS, new StatusDataFilter()],
]);
